<template>
	<header
		aria-label="page-hero"
		class="hero landing-page-hero pb-0 position-relative"
		:style="{ 'background-color': hero.backgroundColor.hex }"
	>
		<div class="container position-relative pt-400-lg pb-400-lg pt-300 pb-300">
			<div
				:class="[
					'hero-content display-flex align-items-center flex-wrap-wrap justify-content-center',
					{ 'gutters-300-md': !thirdPartyAuthStore.isSignupEnabled }
				]"
			>
				<div class="col-12 col-6-md">
					<div v-if="showBrandLogos" class="display-flex align-items-center">
						<BaseLink :link="logoLink">
							<img :src="logoUrl" class="logo-width" alt="Fundrise logo" />
						</BaseLink>
						<div v-if="showPartnerLogo" class="ml-100 pl-100 bl campaign-logo" data-test="partner-logo">
							<img :src="partnerLogoUrl" class="img-native-width" alt="partner logo" />
						</div>
					</div>

					<h1
						v-reveal:fade-up
						class="heading-xl mt-300"
						:class="{ 'text-color-white': isDarkTheme }"
						data-test="hero-heading"
					>
						{{ hero.heading }}
					</h1>
					<p
						v-if="hero.description"
						class="body-xl-md body-lg font-weight-lighter"
						:class="{ 'text-color-white': isDarkTheme }"
						data-test="hero-description"
					>
						{{ hero.description }}
					</p>
					<div class="mt-200">
						<h2
							v-if="hero.showLeadCapture && hero.leadCaptureHeading"
							class="subheading mb-50"
							:class="{ 'text-color-white': isDarkTheme }"
							data-test="lead-capture-heading"
						>
							{{ hero.leadCaptureHeading }}
						</h2>
						<p
							v-if="hero.leadCaptureSubheading"
							class="body-lg mt-0 mb-100"
							:class="{ 'text-color-white': isDarkTheme }"
							>{{ hero.leadCaptureSubheading }}</p
						>
						<ol v-if="hero.leadCaptureMetadata?.length" class="metadata-stats mt-100 mb-150">
							<li
								v-for="(metadata, index) in parsedMetadata"
								:key="index"
								:class="['metadata-stat', { 'bl pl-150': index !== 0 }]"
							>
								<div>
									<div
										:class="[
											'display-flex gap-25 align-items-center',
											isDarkTheme ? 'text-color-white' : 'text-color-content-secondary'
										]"
									>
										<div v-if="metadata.number" class="metadata-stat__number ff-ivar-display">{{
											metadata.number
										}}</div>
										<div
											v-if="metadata.unit || metadata.text"
											class="metadata-stat__aside display-flex flex-direction-column body-xs"
										>
											<div v-if="metadata.unit" class="line-height-100">{{ metadata.unit }}</div>
											<div v-if="metadata.text" class="line-height-100">{{ metadata.text }}</div>
										</div>
									</div>
									<div
										v-if="metadata.subheading"
										:class="[
											'body-sm mt-25',
											isDarkTheme ? 'text-color-white' : 'text-color-content-tertiary'
										]"
									>
										{{ metadata.subheading }}
									</div>
								</div>
							</li>
						</ol>
						<LeadCaptureComponent v-if="hero.showLeadCapture" :form-config="leadCaptureConfig" />
					</div>
				</div>
				<div
					class="col-12 col-6-md pt-0-md pt-300"
					:class="{
						'order--1-md pr-300-md': !hero.isHeroImageRightAligned,
						'pl-300-md': thirdPartyAuthStore.isSignupEnabled
					}"
				>
					<img :src="heroImgUrl" alt="" role="presentation" fetchpriority="high" />
				</div>
			</div>
		</div>
		<slot name="hero-disclaimer">
			<RichTextComponent
				v-if="heroDisclaimer.length"
				:blocks="heroDisclaimer"
				:class="[
					'landing-page-hero-disclaimer container pb-200 body-sm',
					isDarkTheme ? 'muted-reversed' : 'muted'
				]"
			/>
		</slot>
	</header>
</template>
<script setup lang="ts">
import { LeadCaptureMetadata, SanityLandingHero } from 'types/sanity/modular-landing-page';
import { appCloudfrontPath } from '@constants';
import { computed } from 'vue';
import LeadCaptureComponent from '@components/lead-capture/lead-capture-form.vue';
import { LeadCaptureFormConfig } from 'types/layout/lead';
import { Link } from 'types/layout';
import RichTextComponent from '@components/sanity/rich-text-component.vue';
import { SanityRichTextBlock } from 'types/sanity';
import { useAppStore } from '@stores/app';
import { useSanityImage } from '@utils/composables/use-sanity-image';
import { useThirdPartyAuthStore } from '@stores/third-party-auth';

interface LeadCaptureParsedMetadata {
	number?: string;
	unit?: string;
	text?: string;
	subheading?: string;
}

interface Props {
	hero: SanityLandingHero;
	leadCaptureFeeWaiver?: string;
	showBrandLogos?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
	leadCaptureFeeWaiver: undefined,
	showBrandLogos: true
});

const appStore = useAppStore();
const thirdPartyAuthStore = useThirdPartyAuthStore();

const leadCaptureConfig = computed((): LeadCaptureFormConfig => {
	const config: LeadCaptureFormConfig = {
		textCssClass: 'delta',
		buttonText: props.hero.leadCaptureButtonText,
		signupFlowType: 'STANDARD',
		thirdPartyAuthTheme: props.hero.thirdPartyAuthTheme ?? undefined
	};

	if (props.hero?.presetPlanID) {
		config.signupFlowType = 'PRESET_PLAN';
		config.presetPlanId = props.hero.presetPlanID;
	}

	return config;
});

const isDarkTheme = computed((): boolean => {
	return props.hero.isDarkTheme;
});

const heroImgUrl = computed(() => useSanityImage(props.hero.image.asset.url, 'hero'));

const logoLink = computed((): Link => {
	return appStore.isAuthenticated ? { router: 'account-overview' } : { router: 'home' };
});

const logoUrl = computed((): string => {
	const logo = isDarkTheme.value ? 'logo-hz-white' : 'logo-hz-color';
	return `${appCloudfrontPath}/interface/${logo}.svg`;
});

const showPartnerLogo = computed((): boolean => {
	return !!props.hero.campaignLogo?.asset.url;
});

const partnerLogoUrl = computed((): string => {
	return props.hero.campaignLogo?.asset.url ?? '';
});

const heroDisclaimer = computed((): Array<SanityRichTextBlock> => {
	return props.hero.disclaimer ?? [];
});

const parsedMetadata = computed((): Array<LeadCaptureParsedMetadata> => {
	const parseMetadataHeading = (metadata?: LeadCaptureMetadata): LeadCaptureParsedMetadata | null => {
		if (!metadata || !metadata.leadCaptureMetadataHeading) return null;

		const match = metadata.leadCaptureMetadataHeading.match(
			/^(?:(\d+)\s)?((?:month|year|day|week|quarter)s?)?(.*)$/
		);

		const [, number, unit, text] = match ?? [];

		return { number, unit, text, subheading: metadata.leadCaptureMetadataSubheading };
	};

	if (!props.hero.leadCaptureMetadata) {
		return [];
	}
	return (props.hero.leadCaptureMetadata.map((metadata) => parseMetadataHeading(metadata)).filter(Boolean) ??
		[]) as Array<LeadCaptureParsedMetadata>;
});

async function setup(): Promise<void> {
	if (props.leadCaptureFeeWaiver) {
		leadCaptureConfig.value.feeWaiver = props.leadCaptureFeeWaiver;
	}
}
setup();
</script>
<style lang="scss">
.landing-page-hero {
	.logo-width {
		width: 137px;
	}
	.campaign-logo img {
		max-height: 30px;
	}

	&-disclaimer {
		a {
			color: inherit;
			text-decoration: underline;
		}
	}

	.metadata-stats {
		display: flex;
		gap: 1.5rem;
		line-height: 1;

		.metadata-stat {
			display: flex;
			gap: 0.25rem;
			align-items: center;

			&__number {
				font-size: 2rem;
			}

			&__aside {
				gap: 0.125rem;
			}
		}
	}
}
</style>
